import React, { FC, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Layout as AntLayout } from "antd";
import { ReactTyped } from "react-typed";
import Head from "./head/Head";
import HeaderMain from "./header/HeaderMain";
import Footer from "./footer/Foooter";
import MobileNavigation from "./navigation/MobileNavigation";
import { HEADER_HEIGHT } from "./constants";

const { Content } = AntLayout;

interface PaymentAlertDetails {
  overdueCount: number;
  totalDueAmount: number;
  oldestDueDate?: string;
  autoHoldEnabled?: boolean;
  autoHoldDate?: string;
}

const fmtDate = (dt: string) =>
  new Date(dt).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

// Login stashes overdue-invoice details in sessionStorage; the banner below renders
// them. Disabled — flip to true to bring the payment-due banner back.
const SHOW_PAYMENT_DUE_BANNER = false;

const fmtAmt = (v: number) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

interface LayoutDefaultProps {
  title?: string;
}

const LayoutDefault: FC<LayoutDefaultProps> = ({ title }) => {
  const [paymentAlert, setPaymentAlert] = useState<PaymentAlertDetails | null>(
    null
  );
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  useEffect(() => {
    const alertData = sessionStorage.getItem("pending_payment_alert");
    if (!SHOW_PAYMENT_DUE_BANNER) {
      // Still drain the key so a stale alert can't resurface later in the session.
      sessionStorage.removeItem("pending_payment_alert");
      return;
    }
    if (alertData) {
      sessionStorage.removeItem("pending_payment_alert");
      try {
        const parsed = JSON.parse(alertData);
        const d: PaymentAlertDetails = parsed.details || {};
        setPaymentAlert(d);
        setIsAlertOpen(true);
      } catch {}
    }
  }, []);

  const buildSingleString = (d: PaymentAlertDetails): string => {
    const parts: string[] = [
      `<strong>Pending Invoices:</strong> ${d.overdueCount || 0}`,
      `<strong>Total Due:</strong> <span style="color:#c0392b;font-weight:700">₹ ${fmtAmt(
        d.totalDueAmount
      )}</span>`,
    ];
    if (d.oldestDueDate) {
      parts.push(
        `<strong>Overdue Since:</strong> ${fmtDate(d.oldestDueDate)}`
      );
    }
    if (
      d.autoHoldEnabled &&
      d.autoHoldDate &&
      new Date(d.autoHoldDate) > new Date()
    ) {
      parts.push(
        `<strong style="color:#c0392b">Account Hold:</strong> <span style="color:#c0392b">${fmtDate(
          d.autoHoldDate
        )}</span>`
      );
    }
    parts.push(
      `<span style="color:#856404">Please contact your administrator.</span>`
    );
    return parts.join("&nbsp;&nbsp;|&nbsp;&nbsp;");
  };

  const handleDismiss = () => setIsAlertOpen(false);

  return (
    <>
      <Head title={title || "Project Management"} />

      {paymentAlert && isAlertOpen && (
        <>
          {/* Blur/dim overlay — click anywhere to dismiss */}
          <div
            onClick={handleDismiss}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1058,
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              backgroundColor: "rgba(0, 0, 0, 0.35)",
              cursor: "pointer",
            }}
          />

          {/* Payment-due banner — pinned above sticky header */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1059,
              background:
                "linear-gradient(90deg, #fff3cd 0%, #ffe08a 50%, #fff3cd 100%)",
              borderBottom: "2px solid #f0ad4e",
              boxShadow: "0 3px 14px rgba(0,0,0,0.22)",
              padding: "10px 48px 10px 16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              minHeight: "42px",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#e67e22",
                color: "#fff",
                borderRadius: "6px",
                padding: "3px 10px",
                fontWeight: 700,
                fontSize: "12px",
                letterSpacing: "0.5px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              ⚠ PAYMENT DUE
            </span>
            <span style={{ fontSize: "13px", color: "#533f03", flex: 1 }}>
              <ReactTyped
                strings={[buildSingleString(paymentAlert)]}
                typeSpeed={18}
                showCursor={false}
                loop={false}
              />
            </span>
            <button
              onClick={handleDismiss}
              aria-label="Close"
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "18px",
                color: "#856404",
                lineHeight: 1,
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              ×
            </button>
          </div>
        </>
      )}

      <AntLayout
        style={{
          minHeight: "100vh",
          background: "#f5f6fa",
        }}
      >
        {/* Fixed Header with Main Navigation */}
        <HeaderMain fixed showNavigation />

        {/* Mobile Navigation Drawer */}
        <MobileNavigation />

        {/* Main Content Area */}
        <Content
          className="pms-main-content"
          style={{
            marginTop: HEADER_HEIGHT,
            padding: "8px 24px 24px",
            minHeight: `calc(100vh - ${HEADER_HEIGHT}px - 50px)`,
          }}
        >
          <Outlet />
        </Content>

        {/* Footer */}
        <Footer />
      </AntLayout>
    </>
  );
};

export default LayoutDefault;
